// The `!` chooser (U0): "Should have been a card", "Shouldn't be here", "Draft needed rewriting"
// (only where a draft exists), an optional short note, then Save. Keys inside: 1–3 choose, Enter
// saves, Esc closes. The verdict goes to the audit log for the shadow week's evals; nothing about
// the card or the mailbox changes.
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { VERDICT_LABEL, VERDICT_NOTE_MAX, type Verdict } from "@shared/measure";
import { isApiError } from "@web/lib/api";
import { cn } from "@web/lib/cn";
import { measureApi } from "@web/lib/measureApi";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { Modal } from "@web/ui/Modal";
import { offerTeach } from "@web/teach/teachStore";
import { verdictStore, useVerdictState } from "./verdictStore";
import { verdictChoices, verdictRequest, type VerdictTarget } from "./verdicts";

/** Mounted once in the shell; opens over any screen. */
export function VerdictHost() {
  const state = useVerdictState();
  return (
    <Modal open={state.open} onClose={verdictStore.close} label="What did MeeMail get wrong?">
      {state.open && state.target ? (
        <VerdictChooser key={state.seq} target={state.target} onClose={verdictStore.close} />
      ) : null}
    </Modal>
  );
}

function failureWords(error: unknown): string {
  if (isApiError(error, "not_found")) return "That thread or card isn’t here any more.";
  if (isApiError(error, "invalid_request"))
    return error.serverMessage ? `${error.serverMessage}.` : "That verdict doesn’t fit here.";
  return "Couldn’t save that just now. Nothing changed.";
}

function VerdictChooser({ target, onClose }: { target: VerdictTarget; onClose: () => void }) {
  const choices = verdictChoices(target);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [threadKey, setThreadKey] = useState(target.threads[0]!.threadKey);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLInputElement>(null);

  useEffect(() => rootRef.current?.focus({ preventScroll: true }), []);

  const choose = (v: Verdict) => {
    setVerdict(v);
    noteRef.current?.focus();
  };

  const save = async () => {
    if (!verdict || busy) return;
    setBusy(true);
    try {
      await measureApi.verdict(verdictRequest(target, verdict, threadKey, note));
      toast(`Noted: ${VERDICT_LABEL[verdict].toLowerCase()}. It’s in the audit log for the evals.`);
      onClose();
      // U7: just this once, or every time? (defaults to just this once)
      offerTeach({
        threadKey,
        surface: "verdict",
        verdict,
        title: target.threads.find((t) => t.threadKey === threadKey)?.subject ?? target.title,
        ...(target.cardId ? { cardId: target.cardId } : {}),
      });
    } catch (error) {
      toast(failureWords(error));
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && verdict && !(e.target instanceof HTMLButtonElement)) {
      e.preventDefault();
      void save();
      return;
    }
    if (e.target instanceof HTMLInputElement) return;
    const picked = choices[Number(e.key) - 1];
    if (picked && !e.repeat) {
      e.preventDefault();
      choose(picked);
    }
  };

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="flex max-h-[85dvh] flex-col outline-none"
      data-testid="verdict-chooser"
    >
      <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <p className="mm-kicker">What did MeeMail get wrong?</p>
          <h2 className="mt-1 text-xl leading-snug break-words">{target.title}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-1 text-muted hover:bg-sunk hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">
        {target.threads.length > 1 ? (
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-sm font-medium text-ink">Which thread?</legend>
            {target.threads.map((t) => (
              <label key={t.threadKey} className="flex items-center gap-2 text-sm text-ink-2">
                <input
                  type="radio"
                  name="verdict-thread"
                  checked={t.threadKey === threadKey}
                  onChange={() => setThreadKey(t.threadKey)}
                />
                <span className="truncate">{t.subject}</span>
              </label>
            ))}
          </fieldset>
        ) : null}
        <div role="radiogroup" aria-label="Verdict" className="flex flex-col gap-1.5">
          {choices.map((v, i) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={verdict === v}
              onClick={() => choose(v)}
              className={cn(
                "flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left",
                verdict === v
                  ? "border-accent/50 bg-accent-soft"
                  : "border-line bg-bg hover:bg-sunk/60",
              )}
            >
              <span className="text-ink">{VERDICT_LABEL[v]}</span>
              <Kbd className="hidden sm:inline-flex">{String(i + 1)}</Kbd>
            </button>
          ))}
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted">A short note, if it helps (optional)</span>
          <input
            ref={noteRef}
            value={note}
            maxLength={VERDICT_NOTE_MAX}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. my landlord always writes from this address"
            className="h-9 rounded-md border border-line-strong bg-bg px-2.5 text-ink outline-none focus:border-accent"
          />
        </label>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-muted">
            <Kbd>1</Kbd>–<Kbd>{String(choices.length)}</Kbd> choose · <Kbd>Enter</Kbd> save ·{" "}
            <Kbd>Esc</Kbd> close
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              busy={busy}
              disabled={!verdict}
              onClick={() => void save()}
            >
              Save
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted">
          Kept on this machine in the audit log. It doesn’t change the card or your mail.
        </p>
      </div>
    </div>
  );
}
